import { useState } from 'react'
import { Sparkles, FileText, Layers, ChevronRight } from 'lucide-react'
import { ImportanceBadge } from './ImportanceBadge'
import { cn } from '@/lib/utils'

export interface ReflectionNode {
  memory_id: string
  content_text: string
  memory_type: 'reflection' | 'observation_evidence' | 'pass_output' | 'pre_read_summary' | 'validation_outcome'
  importance_raw: number
  reflection_depth: number
  created_at: number
  cited_memory_ids?: string[]
  children?: ReflectionNode[]
}

interface ReflectionNodeItemProps {
  node: ReflectionNode
  depth?: number
}

function typeIcon(type: ReflectionNode['memory_type']) {
  if (type === 'reflection') return <Sparkles className="h-3.5 w-3.5 text-signal-cyan shrink-0" />
  if (type === 'pre_read_summary') return <Layers className="h-3.5 w-3.5 text-echo-indigo shrink-0" />
  return <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
}

function ReflectionNodeItem({ node, depth = 0 }: ReflectionNodeItemProps) {
  const [expanded, setExpanded] = useState(depth === 0)
  const hasChildren = (node.children?.length ?? 0) > 0
  const isCited = node.reflection_depth === 0

  return (
    <div
      data-testid={isCited ? 'cited-observation' : 'reflection-node'}
      data-memory-id={node.memory_id}
      data-depth={node.reflection_depth}
      className="space-y-1"
      style={{ paddingLeft: depth > 0 ? `${depth * 16}px` : undefined }}
    >
      <div
        className={cn(
          'flex items-start gap-2 rounded-lg border p-3',
          node.memory_type === 'reflection'
            ? 'border-signal-cyan/30 bg-signal-cyan/5'
            : 'border-border-gray bg-white'
        )}
      >
        {hasChildren && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="mt-0.5 shrink-0 text-muted-foreground hover:text-deep-ink transition-colors"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-90')} />
          </button>
        )}
        {!hasChildren && <span className="w-3.5 shrink-0" />}
        {typeIcon(node.memory_type)}
        <div className="flex-1 min-w-0 space-y-1">
          <p className="text-sm text-deep-ink leading-snug">{node.content_text}</p>
          <div className="flex items-center gap-2">
            <ImportanceBadge score={node.importance_raw} />
            {node.reflection_depth > 0 && (
              <span className="text-xs text-muted-foreground">depth {node.reflection_depth}</span>
            )}
            {node.cited_memory_ids && node.cited_memory_ids.length > 0 && (
              <span className="text-xs text-muted-foreground">
                cites {node.cited_memory_ids.length}
              </span>
            )}
          </div>
        </div>
      </div>

      {hasChildren && expanded && (
        <div className="space-y-1">
          {node.children!.map(child => (
            <ReflectionNodeItem key={child.memory_id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

interface ReflectionTreeProps {
  nodes: ReflectionNode[]
  className?: string
}

export function ReflectionTree({ nodes, className }: ReflectionTreeProps) {
  if (!nodes.length) {
    return (
      <div className={cn('rounded-lg border border-border-gray p-6 text-center text-sm text-muted-foreground', className)}>
        No reflections generated yet.
      </div>
    )
  }

  return (
    <div
      data-testid="reflection-tree"
      className={cn('space-y-2', className)}
    >
      {nodes.map(node => (
        <ReflectionNodeItem key={node.memory_id} node={node} />
      ))}
    </div>
  )
}
